import { useState } from "react";
import { X } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { useLocalizedGallery, useLocalizedGalleryCategories } from "@/i18n/hooks";

export default function Gallery() {
    const { t } = useLang();
    const gallery = useLocalizedGallery();
    const categories = useLocalizedGalleryCategories();
    const [active, setActive] = useState(categories[0]);
    const [lightbox, setLightbox] = useState(null);
    const filtered = active === categories[0] ? gallery : gallery.filter((g) => g.category === active);

    return (
        <div data-testid="gallery-page" className="bg-paper">
            <section className="pt-40 md:pt-48 pb-16 container-editorial">
                <SectionLabel label={t("gallery.label")} number="C" className="mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-end">
                    <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight md:col-span-8 whitespace-pre-line">
                        {t("gallery.heading")}
                    </h1>
                    <p className="md:col-span-4 text-ink/70 text-lg leading-relaxed">
                        {t("gallery.intro")}
                    </p>
                </div>
            </section>

            <section className="pb-12 container-editorial">
                <div className="flex flex-wrap gap-3">
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setActive(c)}
                            data-testid={`gallery-filter-${String(c).toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-5 py-2 rounded-full text-sm border transition-colors duration-300 ${
                                active === c ? "bg-forest text-paper border-forest" : "border-border text-ink/70 hover:border-ink hover:text-ink"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            <section className="pb-32 container-editorial">
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:balance]">
                    {filtered.map((img, i) => (
                        <Reveal key={img.id} delay={i * 50}>
                            <button
                                onClick={() => setLightbox(img)}
                                data-testid={`gallery-item-${img.id}`}
                                className="group block mb-4 md:mb-6 w-full break-inside-avoid overflow-hidden rounded-[12px] relative bg-bone"
                            >
                                <div className={`w-full ${img.aspect} overflow-hidden`}>
                                    <img src={img.url} alt={img.caption} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1600ms] ease-editorial" />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                                    <div>
                                        <div className="overlabel text-gold-light text-[10px]">{img.category}</div>
                                        <div className="font-serif text-white text-xl mt-1">{img.caption}</div>
                                    </div>
                                </div>
                            </button>
                        </Reveal>
                    ))}
                </div>
            </section>

            {lightbox && (
                <div className="fixed inset-0 bg-ink/95 z-[80] flex items-center justify-center p-4 md:p-16 animate-fade-in" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
                    <button onClick={() => setLightbox(null)} aria-label="Close" data-testid="gallery-lightbox-close" className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors">
                        <X size={28} strokeWidth={1.5} />
                    </button>
                    <div className="max-w-6xl w-full">
                        <img src={lightbox.url.replace("w=1400", "w=2400")} alt={lightbox.caption} className="w-full max-h-[80vh] object-contain rounded-[12px]" />
                        <div className="text-center mt-6">
                            <div className="overlabel text-gold-light">{lightbox.category}</div>
                            <div className="font-serif text-white text-2xl mt-2">{lightbox.caption}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
